import * as analytic from '@anm/analytic';
import PasswordForm from '@anm/components/form/PasswordForm';
import Switch from '@anm/components/form/Switch';
import SectionTitle from '@anm/components/video-landings/SectionTitle';
import getCuttedPassword from '@anm/helpers/getCuttedPassword';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { Password } from '@anm/hosting-player';
import isEqual from 'lodash/fp/isEqual';
import omitBy from 'lodash/fp/omitBy';
import { useCallback, useEffect, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomizationTabIndexes } from '../../../../../pages/video';

import { hostedVideoActions, hostedVideoSelectors } from '../../../../../store/modules/hostedVideo';
import { ChangeHandlers } from '../../../types';
import useTogglers from '../../hooks/useTogglers';

import { PasswordSwitch, Wrapper } from './Wrapper';

type PermissionsTabProps = ChangeHandlers & {
  hostId: string;
};

const PermissionsTab: FC<PermissionsTabProps> = ({ hostId, wasChanged, getHandlers }) => {
  const currentPassword = useSelector(hostedVideoSelectors.selectVideoPassword);
  const { save, isPending } = getHandlers();
  const [togglers] = useTogglers<CustomizationTabIndexes>({ hostId });
  const dispatch = useDispatch();

  const formInitialValues = {
    password: getCuttedPassword(currentPassword?.password),
    description: currentPassword?.description
  };

  const disabled = !currentPassword?.isEnabled;

  const updatePassword = (password: Password) => {
    dispatch(
      hostedVideoActions.updateVideoPassword({
        hostId,
        security: {
          ...currentPassword,
          ...password
        }
      })
    );
  };

  const handleFormBlur = useCallback(
    (password: Password) => {
      const cuttedCurrentPassword: Password = omitBy(k => typeof k === 'boolean')({
        ...currentPassword,
        password: getCuttedPassword(currentPassword?.password)
      });

      const canUpdatePassword = !isEqual(password, cuttedCurrentPassword);

      canUpdatePassword && updatePassword(password);
    },
    [currentPassword]
  );

  useEffect(() => {
    analytic.trackOpenVideoPassword();
  }, []);

  const { password: passwordToggler } = togglers;
  const { status, handler, ...otherToggleProps } = passwordToggler;
  const showTip =
    !disabled && (currentPassword?.password?.length === 0 || typeof currentPassword?.password === 'undefined');

  return (
    <Wrapper>
      <PasswordSwitch>
        <SectionTitle title="Require password for viewing" level={2} />
        {isMobileOrIPad() && <Switch checked={status} onChange={handler} {...otherToggleProps} />}
      </PasswordSwitch>
      <PasswordForm
        onBlur={handleFormBlur}
        onSubmit={save}
        initialValues={formInitialValues}
        {...{ wasChanged, isPending, showTip }}
      />
    </Wrapper>
  );
};

export default PermissionsTab;
